﻿import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaCatorceM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout
      const padding = 24;
      let y = padding;
      const usableW = Math.max(0, width - padding * 2);

      // -- TITLE SQUARE + TITLE --
      const titleFontSize = opts.titleFontSize || 50;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 18;
      const squareText = opts.titleSquareText || '2.2';
      const squareX = padding;

      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);
      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      const title = opts.title || props.title || '';
      const titleX = squareX + squareSize + squareGap;
      const titleMaxW = Math.min(
        typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 1200,
        Math.max(0, usableW - squareSize - squareGap - 12)
      );

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });
        let yPos = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, yPos);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, yPos);
          }
          yPos += lineH;
        }
        return yPos;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // -- SUBTITLE --
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 24;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 24;
      }

      // -- CHARTS (pie left half | bars right half) --
      const graficaUnoData = opts.graficaUnoData || props.graficaUnoData || [];
      const graficaDosData = opts.graficaDosData || props.graficaDosData || [];
      const graficaTresData = opts.graficaTresData || props.graficaTresData || [
        { categoria: 'LGBTI', total: 0 },
        { categoria: 'Poblacion general', total: 51 },
      ];

      const chartsTopGap = typeof opts.chartsTopGap === 'number' ? opts.chartsTopGap : 20;
      const chartsStartY = y + chartsTopGap;

      const colGap = 12;
      const colW = Math.floor((usableW - colGap * 2) / 3);
      const totalChartsW = colW * 3 + colGap * 2;
      const firstColX = padding + Math.floor((usableW - totalChartsW) / 2);
      const secondColX = firstColX + colW + colGap;
      const thirdColX = secondColX + colW + colGap;

      const computedAreaBottom = Math.max(chartsStartY + 80, height - padding - 240);

      // -- PIE CHART 1 (col 1) --
      const maxPieByWidth = Math.max(0, colW - 16);
      const maxPieByHeight = Math.max(0, computedAreaBottom - chartsStartY - 8);
      const pieDiameter = Math.max(0, Math.min(600, maxPieByWidth, maxPieByHeight));
      const sharedPieR = Math.max(10, Math.round(pieDiameter / 2));
      const pieR = sharedPieR;
      const pieX = firstColX + Math.round(colW / 2);
      const pieY = Math.max(chartsStartY + pieR, computedAreaBottom - pieR);

      const totalVal = graficaUnoData.reduce((s, it) => s + (Number(it.total) || 0), 0) || 1;
      let ang = -Math.PI / 2;
      const palette = opts.palette || ['#506be4', '#06b6d4', '#5f84bb', '#10b981', '#6366f1', '#a78bfa'];
      const contrastFor = (hex) => {
        try {
          const h = hex.replace('#', '');
          const r = parseInt(h.length === 3 ? h[0] + h[0] : h.slice(0, 2), 16);
          const g = parseInt(h.length === 3 ? h[1] + h[1] : h.slice(2, 4), 16);
          const b = parseInt(h.length === 3 ? h[2] + h[2] : h.slice(4, 6), 16);
          const lum = 0.299 * r + 0.587 * g + 0.114 * b;
          return lum < 140 ? '#ffffff' : '#0f172a';
        } catch (e) { return '#ffffff'; }
      };

      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        const v = Number(it.total) || 0;
        const a = (v / totalVal) * Math.PI * 2;
        const startAng = ang;
        const endAng = ang + a;
        ctx.beginPath();
        ctx.moveTo(pieX, pieY);
        const fillColor = palette[i % palette.length];
        ctx.fillStyle = fillColor;
        ctx.arc(pieX, pieY, pieR, startAng, endAng);
        ctx.lineTo(pieX, pieY);
        ctx.fill();
        const mid = startAng + a / 2;
        const labelRadius = Math.max(12, Math.round(pieR * 0.62));
        const lx = pieX + Math.cos(mid) * labelRadius;
        const ly = pieY + Math.sin(mid) * labelRadius;
        const pct = Math.round((v / totalVal) * 1000) / 10;
        const labelText = (Number.isFinite(v) ? String(v) : '') + (typeof v === 'number' ? ` (${pct}%)` : '');
        ctx.save();
        ctx.font = `${Math.max(10, Math.round(pieR * 0.12))}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = contrastFor(fillColor);
        if (a > 0.12) {
          ctx.fillText(labelText, lx, ly);
        } else {
          ctx.beginPath();
          ctx.fillStyle = contrastFor(fillColor);
          ctx.arc(pieX + Math.cos(mid) * (pieR + 8), pieY + Math.sin(mid) * (pieR + 8), 3, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
        ang += a;
      }

      // pie legend
      ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let pieLegendY = computedAreaBottom + 8;
      const legendX = firstColX + 8;
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        ctx.fillStyle = palette[i % palette.length];
        ctx.fillRect(legendX, pieLegendY, 12, 12);
        ctx.fillStyle = opts.legendColor || '#111827';
        ctx.fillText(`${it.sexoNombre} (${it.total})`, legendX + 18, pieLegendY - 2);
        pieLegendY += 18;
      }

      // divider between chart columns
      const dividerX = firstColX + colW + Math.round(colGap / 2) - 1;
      ctx.fillStyle = opts.leftSplitDivider || '#e5e7eb';
      ctx.fillRect(dividerX, chartsStartY - 8, 2, Math.max(0, pieLegendY - (chartsStartY - 8) + 8));

      const dividerX2 = secondColX + colW + Math.round(colGap / 2) - 1;
      ctx.fillRect(dividerX2, chartsStartY - 8, 2, Math.max(0, pieLegendY - (chartsStartY - 8) + 8));

      // -- BAR CHART (col 2) --
      const barsX = secondColX + 5;
      const barsW = Math.max(0, colW - 16);
      const barAreaW = Math.max(40, barsW - 16);

      const data = graficaDosData.map(it => ({
        grupoEtareo: it.grupoEtareo || '',
        hombres: Number(it.hombres ?? 0) || 0,
        mujeres: Number(it.mujeres ?? 0) || 0,
      }));

      const maxTotal = Math.max(1, ...data.map(d => d.hombres + d.mujeres));
      const n = Math.max(1, data.length);
      const gap = 15;
      const areaH = Math.max(120, computedAreaBottom - chartsStartY);
      const itemH = Math.max(13, Math.floor((areaH - (n - 1) * gap) / n));

      for (let i = 0; i < data.length; i++) {
        const it = data[i];
        const iy = chartsStartY + i * (itemH + gap);
        const bx = barsX + 8;
        const by = iy;
        const bh = itemH;
        ctx.fillStyle = opts.barBg || '#e6e7ea';
        ctx.fillRect(bx, by, barAreaW, bh);
        const hombresW = Math.min(Math.round((it.hombres / maxTotal) * barAreaW), Math.round(barAreaW / 2));
        const mujeresW = Math.min(Math.round((it.mujeres / maxTotal) * barAreaW), Math.round(barAreaW / 2));
        const centerX = bx + Math.round(barAreaW / 2);
        ctx.fillStyle = opts.colorAct || '#06b6d4';
        ctx.fillRect(centerX - hombresW, by + Math.round(bh * 0.15), hombresW, Math.round(bh * 0.7));
        ctx.fillStyle = opts.colorAnt || '#121096';
        ctx.fillRect(centerX, by + Math.round(bh * 0.15), mujeresW, Math.round(bh * 0.7));
        ctx.font = `${Math.max(8, opts.itemLabelSize || 11)}px Inter, Arial, sans-serif`;
        ctx.textBaseline = 'bottom';
        ctx.textAlign = 'center';
        ctx.fillStyle = opts.itemLabelColor || '#0f172a';
        let labelText = String(it.grupoEtareo || '');
        const maxCategoryW = barAreaW - 6;
        while (ctx.measureText(labelText).width > maxCategoryW && labelText.length > 0) labelText = labelText.slice(0, -1);
        if (labelText.length < (it.grupoEtareo || '').length && labelText.length > 0)
          labelText = labelText.slice(0, Math.max(0, labelText.length - 2)) + '...';
        if (labelText) ctx.fillText(labelText, centerX, by);
        ctx.font = `bold ${Math.max(7, opts.itemLabelSize || 10)}px Inter, Arial, sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#ffffff';
        if (hombresW > 25) ctx.fillText(String(it.hombres), centerX - Math.round(hombresW / 2), by + Math.round(bh / 2));
        if (mujeresW > 25) ctx.fillText(String(it.mujeres), centerX + Math.round(mujeresW / 2), by + Math.round(bh / 2));
      }

      // bars legend
      const totalHombres = data.reduce((s, d) => s + d.hombres, 0);
      const totalMujeres = data.reduce((s, d) => s + d.mujeres, 0);
      const barsLegendY = computedAreaBottom + 12;
      ctx.font = `${opts.legendFontSize || 12}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillStyle = opts.colorAct || '#06b6d4';
      ctx.fillRect(barsX, barsLegendY, 12, 12);
      ctx.fillStyle = opts.legendColor || '#374151';
      ctx.fillText(`Hombres (${totalHombres})`, barsX + 18, barsLegendY);
      const mujeresLegendX = barsX + 160;
      ctx.fillStyle = opts.colorAnt || '#f59e0b';
      ctx.fillRect(mujeresLegendX, barsLegendY, 12, 12);
      ctx.fillStyle = opts.legendColor || '#374151';
      ctx.fillText(`Mujeres (${totalMujeres})`, mujeresLegendX + 18, barsLegendY);

      const barsLegendRowH = Math.round(opts.legendFontSize || 12) + 4;
      const captionTopGap = typeof opts.captionTopGap === 'number' ? opts.captionTopGap : 16;

      // captions under respective charts
      const textoUno = opts.textoUno || props.textoUno || '';
      const textoDos = opts.textoDos || props.textoDos || '';
      const textoTres = opts.textoTres || props.textoTres || '';
      const captionFontSize = opts.bottomFontSize || 20;
      ctx.font = `${captionFontSize}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let ni = 0; ni < words.length; ni++) {
          const test = line ? line + ' ' + words[ni] : words[ni];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[ni];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      const captionY = Math.max(pieLegendY, barsLegendY + barsLegendRowH) + captionTopGap;
      let afterCaptionY = captionY;
      if (textoUno) afterCaptionY = Math.max(afterCaptionY, drawSmallWrap(ctx, textoUno, firstColX + 8, captionY, colW - 16, captionFontSize + 4));
      if (textoDos) afterCaptionY = Math.max(afterCaptionY, drawSmallWrap(ctx, textoDos, barsX, captionY, barsW - 8, captionFontSize + 4));

      const textoRightRaw = opts.textoRight ?? opts.rightText ?? props.textoRight ?? props.rightText ?? '';
      const rightParagraphs = [];
      if (Array.isArray(textoRightRaw)) {
        for (let p of textoRightRaw) if (String(p || '').trim()) rightParagraphs.push(String(p));
      } else if (String(textoRightRaw || '').trim()) {
        rightParagraphs.push(String(textoRightRaw));
      }

      // -- PIE CHART 3 (col 3) --
      if (graficaTresData.length) {
        const thirdChartCenterX = thirdColX + Math.round(colW / 2);
        const thirdChartCenterY = pieY;
        const thirdPieR = sharedPieR;

        const totalTres = graficaTresData.reduce((s, it) => s + (Number(it.total) || 0), 0) || 1;
        let angTres = -Math.PI / 2;
        const paletteTres = opts.paletteTres || ['#06b6d4', '#1e3a8a', '#10b981', '#f59e0b'];

        for (let i = 0; i < graficaTresData.length; i++) {
          const it = graficaTresData[i] || {};
          const v = Number(it.total) || 0;
          const a = (v / totalTres) * Math.PI * 2;
          const startAng = angTres;
          const endAng = angTres + a;

          ctx.beginPath();
          ctx.moveTo(thirdChartCenterX, thirdChartCenterY);
          const fillColor = paletteTres[i % paletteTres.length];
          ctx.fillStyle = fillColor;
          ctx.arc(thirdChartCenterX, thirdChartCenterY, thirdPieR, startAng, endAng);
          ctx.lineTo(thirdChartCenterX, thirdChartCenterY);
          ctx.fill();

          const mid = startAng + a / 2;
          const labelRadius = Math.max(12, Math.round(thirdPieR * 0.62));
          const lx = thirdChartCenterX + Math.cos(mid) * labelRadius;
          const ly = thirdChartCenterY + Math.sin(mid) * labelRadius;
          const pct = Math.round((v / totalTres) * 1000) / 10;
          const labelText = `${v} (${pct}%)`;

          ctx.save();
          ctx.font = `${Math.max(10, Math.round(thirdPieR * 0.13))}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = contrastFor(fillColor);
          if (a > 0.12) ctx.fillText(labelText, lx, ly);
          ctx.restore();

          angTres += a;
        }

        ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        let thirdLegendY = computedAreaBottom + 8;
        const thirdLegendX = thirdColX + 8;
        for (let i = 0; i < graficaTresData.length; i++) {
          const it = graficaTresData[i] || {};
          ctx.fillStyle = paletteTres[i % paletteTres.length];
          ctx.fillRect(thirdLegendX, thirdLegendY, 12, 12);
          ctx.fillStyle = opts.legendColor || '#111827';
          ctx.fillText(`${it.categoria || ''} (${Number(it.total) || 0})`, thirdLegendX + 18, thirdLegendY - 2);
          thirdLegendY += 18;
        }

        const thirdCaptionY = thirdLegendY + captionTopGap;
        ctx.font = `${captionFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.bottomColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        if (textoTres) afterCaptionY = Math.max(afterCaptionY, drawSmallWrap(ctx, textoTres, thirdColX + 8, thirdCaptionY, colW - 16, captionFontSize + 4));
        else afterCaptionY = Math.max(afterCaptionY, thirdLegendY + 12);
      }

      y = afterCaptionY + 8;

      // -- TEXTO RIGHT (below charts, full width) --
      if (rightParagraphs.length) {
        const rightFontSize = typeof opts.textoRightSize === 'number' ? opts.textoRightSize : 28;
        ctx.font = `${rightFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.textoRightColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const lineH = Math.round(rightFontSize * 1.3);
        let textY = y;
        for (let p of rightParagraphs) {
          textY = drawSmallWrap(ctx, p, padding, textY, usableW, lineH);
          textY += Math.round(lineH * 0.4);
        }
      }

      // -- FOOTER --
      const footerText = opts.footerText || props.footerText || '';
      if (footerText) {
        ctx.fillStyle = opts.footerColor || '#6b7280';
        ctx.font = `${opts.footerFontSize ?? 25}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(footerText, padding + usableW, height - padding);
      }

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_catorce_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});